// Now officially integrated into Vuetify:
//
//   https://github.com/vuetifyjs/vuetify/blob/master/src/components/VApp/mixins/app-breakpoint.js
//   https://github.com/vuetifyjs/vuetify/blob/master/src/components/VApp/mixins/app-breakpoint.spec.js

/**
 * A Vue mixin to get the current width/height and the associated breakpoint.
 *
 * Useful to e.g. adapt the user interface from inside a Vue component
 * as opposed to using CSS classes. The breakpoint pixel values and
 * range names are taken from Vuetify (https://github.com/vuetifyjs).
 *
 * Use within a component:
 *
 *   import breakpoint from './breakpoint.js';
 *
 *   export default {
 *     name: 'my-component',
 *     mixins: [breakpoint],
 *     ...
 *
 * Then inside a template:
 *
 *   <div v-if="$breakpoint.smAndDown">...</div>
 */

const breakpoint = {
  data() {
    return {
      clientWidth: 0,
      clientHeight: 0
    }
  },
  computed: {
    $breakpoint() {
      const xs = this.clientWidth < 576
      const sm = this.clientWidth < 768 && !xs
      const md = this.clientWidth < 992 && !(sm || xs)
      const lg = this.clientWidth < 1200 && !(md || sm || xs)
      const xl = this.clientWidth >= 1200 && !(lg || md || sm || xs)

      const xsOnly = xs
      const smOnly = sm
      const smAndDown = (xs || sm) && !(md || lg || xl)
      const smAndUp = !xs && (sm || md || lg || xl)
      const mdOnly = md
      const mdAndDown = (xs || sm || md) && !(lg || xl)
      const mdAndUp = !(xs || sm) && (md || lg || xl)
      const lgOnly = lg
      const lgAndDown = (xs || sm || md || lg) && !xl
      const lgAndUp = !(xs || sm || md) && (lg || xl)
      const xlOnly = xl

      let name
      switch (true) {
        case xs:
          name = 'xs'
          break
        case sm:
          name = 'sm'
          break
        case md:
          name = 'md'
          break
        case lg:
          name = 'lg'
          break
        default:
          name = 'xl'
          break
      }

      const result = {
        // Definite breakpoint.
        xs,
        sm,
        md,
        lg,
        xl,

        // Useful e.g. to construct CSS class names dynamically.
        name,

        // Breakpoint ranges.
        xsOnly,
        smOnly,
        smAndDown,
        smAndUp,
        mdOnly,
        mdAndDown,
        mdAndUp,
        lgOnly,
        lgAndDown,
        lgAndUp,
        xlOnly,

        // For custom breakpoint logic.
        width: this.clientWidth,
        height: this.clientHeight
      }
      return result
    }
  },
  methods: {
    updateDimensions() {
      // Cross-browser support as described in:
      // https://stackoverflow.com/questions/1248081
      this.clientWidth = Math.max(
        document.documentElement.clientWidth,
        window.innerWidth || 0
      )
      this.clientHeight = Math.max(
        document.documentElement.clientHeight,
        window.innerHeight || 0
      )
    }
  },
  created() {
    if (typeof window === 'undefined') {
      return
    }

    this.updateDimensions()
    window.addEventListener('resize', this.updateDimensions, {
      passive: true
    })
  },
  destroyed() {
    if (typeof window === 'undefined') {
      return
    }
    window.removeEventListener('resize', this.updateDimensions)
  }
}

export default breakpoint
